import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api_getByParentCategoryId, api_getSizesByCategoryId } from '~/backend/api_calls';
import Button from '~/components/common/Button';
import Modal from '~/components/common/Modal';
import { FiltersModalLoader } from '~/components/common/SkeletonLoaders';

const HomepageFiltersModal = ({ filterModalVisible, setFilterModalVisible, category }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [sizesPerCategories, setSizesPerCategories] = useState([]);
	const [selectedSizes, setSelectedSizes] = useState([]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoading(true);
				const response = await api_getByParentCategoryId(category.id);
				const categories = response.items;

				const data = await Promise.all(
					categories.map(async category => {
						// Make individual API request for each item
						const sizes = await api_getSizesByCategoryId(category.id);
						const sizesPerCategory = { ...category, sizes: sizes.length > 0 ? sizes[0].items : [] };
						return sizesPerCategory;
					})
				);

				setSizesPerCategories(data);
				setSelectedSizes(
					data.map(x => {
						return {
							categoryId: x.id,
							categoryUrl: x.eurl,
							sizes: []
						};
					})
				);
				setLoading(false);
			} catch (error) {
				console.error('Error fetching data:', error);
				setLoading(false);
			}
		};

		fetchData();
	}, [category]);

	const handleToggleFilter = (categoryId, toggledSize) => {
		// Check if the clicked value is already selected
		const isSelected = selectedSizes.find(category => category.categoryId === categoryId)?.sizes?.find(size => size.id === toggledSize.id);

		// Update the selectedSizes array based on the clicked value
		if (isSelected) {
			const category = selectedSizes.find(category => category.categoryId === categoryId);
			const sizes = category.sizes.filter(x => x.id !== toggledSize.id);
			category.sizes = sizes;

			setSelectedSizes([...selectedSizes.filter(category => category.categoryId !== categoryId), category]);
		} else {
			const category = selectedSizes.find(category => category.categoryId === categoryId);
			const sizes = [...category.sizes, toggledSize];
			category.sizes = sizes;

			setSelectedSizes([...selectedSizes.filter(category => category.categoryId !== categoryId), category]);
		}
	};

	const handleApplyFilters = () => {
		const categories = selectedSizes.filter(x => x.sizes.length > 0);

		if (categories.length === 1) {
			const url = categories[0].categoryUrl + '?q=size%3A';
			const queryLink = categories[0].sizes.map(item => item.vl).join('%7C');
			navigate(url + queryLink);
		} else if (categories.length > 1) {
			const url = category.eurl + '?q=size%3A';
			const sizes = categories.flatMap(x => x.sizes);
			const uniqueSizes = [];
			const idSet = [];

			sizes.forEach(obj => {
				if (!idSet.find(x => x === obj.id)) {
					idSet.push(obj.id);
					uniqueSizes.push(obj);
				}
			});

			const queryLink = uniqueSizes.map(item => item.vl).join('%7C');

			navigate(url + queryLink);
		} else {
			navigate(category.eurl);
		}
	};

	return (
		<>
			<Modal open={filterModalVisible} setOpen={setFilterModalVisible} className="filtersModal">
				<div className="filtersModal-container">
					<div className="header">
						<div className="title">
							ΦΙΛΤΡΑ ΓΙΑ <span>{category.nm}</span>
						</div>
					</div>
					<div className="body">
						{loading ? (
							<FiltersModalLoader />
						) : (
							sizesPerCategories.map(
								(category, index) =>
									category.sizes.length > 0 && (
										<div key={index} className="filter-type-section">
											<h3>#{category.nm}</h3>
											<div className="filter-values-container">
												{category.sizes.map((filtervalue, index) => (
													<span
														onClick={() => handleToggleFilter(category.id, filtervalue)}
														key={index}
														className={`filter-value ${
															selectedSizes.find(x => x.categoryId === category.id)?.sizes.find(y => y.id === filtervalue.id)
																? 'active'
																: ''
														} ${filtervalue.product_count === 0 ? 'disabled' : ''}`}>
														{filtervalue.vl} ({filtervalue.product_count})
													</span>
												))}
											</div>
										</div>
									)
							)
						)}
					</div>

					<div className="footer">
						<Button outline={true} color="secondary" actionOnClick={() => setFilterModalVisible(false)}>
							<b>ΠΙΣΩ ΣΤΗΝ ΑΡΧΙΚΗ</b>
						</Button>
						<Button color="secondary" actionOnClick={() => handleApplyFilters()}>
							ΕΦΑΡΜΟΓΗ
						</Button>
					</div>
				</div>
			</Modal>
		</>
	);
};

HomepageFiltersModal.propTypes = {
	filterModalVisible: PropTypes.bool,
	setFilterModalVisible: PropTypes.func,
	category: PropTypes.object
};

export default HomepageFiltersModal;
